/**
 * Icons mirrored from the ICE side rail, reproduced from the IBM Carbon Design System.
 * Kept inline rather than added as a dependency — the rail only needs a few of them.
 *
 * Copyright IBM Corp. 2016, 2023
 * Licensed under the Apache License, Version 2.0 (http://www.apache.org/licenses/LICENSE-2.0).
 * See frontend/THIRD_PARTY_NOTICES.md.
 */
type IconProps = {
    size?: number;
    /** Lets the caller override the size responsively; CSS width/height win over the attributes. */
    className?: string;
};

const iconProps = (size: number) => ({
    focusable: false as const,
    preserveAspectRatio: "xMidYMid meet",
    fill: "currentColor",
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    "aria-hidden": true as const,
    xmlns: "http://www.w3.org/2000/svg"
});

/** ICE renders this one at 36px on the bottom bar and 40px on the rail, hence the className. */
export const HomeIcon = ({ size = 36, className }: IconProps) => (
    <svg {...iconProps(size)} className={className}>
        <path d="M16.6123,2.2138a1.01,1.01,0,0,0-1.2427,0L1,13.4194l1.2427,1.5717L4,13.6209V26a2.0041,2.0041,0,0,0,2,2H26a2.0037,2.0037,0,0,0,2-2V13.63L29.7573,15,31,13.4282ZM18,26H14V18h4Zm2,0V18a2.0023,2.0023,0,0,0-2-2H14a2.002,2.002,0,0,0-2,2v8H6V12.0615l10-7.79,10,7.8005V26Z" />
    </svg>
);

/** Carbon's `chat-bot`, the glyph ICE gives this item. */
export const AskAiIcon = ({ size = 36, className }: IconProps) => (
    <svg {...iconProps(size)} className={className}>
        <path d="M16,19a6.9908,6.9908,0,0,1-5.833-3.1287l1.666-1.1074a5.0007,5.0007,0,0,0,8.334,0l1.666,1.1074A6.9908,6.9908,0,0,1,16,19Z" />
        <path d="M20,8a2,2,0,1,0,2,2A1.9806,1.9806,0,0,0,20,8Z" />
        <path d="M12,8a2,2,0,1,0,2,2A1.9806,1.9806,0,0,0,12,8Z" />
        <path d="M17.7358,30,16,29l4-7h6a1.9966,1.9966,0,0,0,2-2V6a1.9966,1.9966,0,0,0-2-2H6A1.9966,1.9966,0,0,0,4,6V20a1.9966,1.9966,0,0,0,2,2h9v2H6a3.9993,3.9993,0,0,1-4-4V6A3.9988,3.9988,0,0,1,6,2H26a3.9988,3.9988,0,0,1,4,4V20a3.9993,3.9993,0,0,1-4,4H21.1646Z" />
    </svg>
);

export const LogOutIcon = ({ size = 28, className }: IconProps) => (
    <svg {...iconProps(size)} className={className}>
        <path d="M6,30H18a2.0023,2.0023,0,0,0,2-2V25H18v3H6V4H18V7h2V4a2.0023,2.0023,0,0,0-2-2H6A2.0023,2.0023,0,0,0,4,4V28A2.0023,2.0023,0,0,0,6,30Z" />
        <path d="M20.586 20.586 24.172 17 10 17 10 15 24.172 15 20.586 11.414 22 10 28 16 22 22 20.586 20.586z" />
    </svg>
);

/**
 * ICE has no Profile entry, so there is no reference icon to mirror.
 * Carbon's user glyph, sized to match Log Out, the footer item it sits above.
 */
export const ProfileIcon = ({ size = 28, className }: IconProps) => (
    <svg {...iconProps(size)} className={className}>
        <path d="M16,4a5,5,0,1,0,5,5A5,5,0,0,0,16,4Zm0,8a3,3,0,1,1,3-3A3.0033,3.0033,0,0,1,16,12Z" />
        <path d="M16,17c-5.5088,0-10,3.5888-10,8v3H8V25c0-3.3086,3.5888-6,8-6s8,2.6914,8,6v3h2V25C26,20.5888,21.5088,17,16,17Z" />
    </svg>
);
